import { influencer } from '@round/api';
import { useCreatorExploreListAllQueries, useCreatorSearch } from '@round/creatorbase-helpers/api-hooks';
import { useInstagramUserImageQueriesByIds } from '@round/platforms/instagram';
import { useTiktokUserImageQueriesByIds } from '@round/platforms/tiktok';
import { ExplorePlatform, PAGE_SIZE } from 'Modules/Plans/CreatorSearch/components/CreatorExploreTable/helpers';
import { extractUserIdsFromData } from 'Modules/Plans/CreatorSearch/utils/creatorSearchImageUtils';
import { isNumber } from 'utility/utility';

type Params = {
    poolId: number | undefined;
    platform: ExplorePlatform | undefined;
    clientId: number | null | undefined;
};

type Config = {
    enabled: boolean;
};

/** Only the count of the unfiltered pool is read, so its page may as well be the smallest one served. */
const POOL_COUNT_PAGE_SIZE = 1;

/**
 * A pool's creators, filtered to the campaign's platform and loaded in full so a
 * selection never depends on how far the table has been scrolled.
 */
export function usePoolCreators({ poolId, platform, clientId }: Params, config: Config = { enabled: true }) {
    const poolParams = {
        pool_id: poolId ? String(poolId) : undefined,
        client_id: clientId ?? undefined,
    };

    const isEnabled = config.enabled && !!poolId && !!platform && isNumber(clientId);

    const { data, count, areAllPagesResolved, isLoading, isFetching, hasError } = useCreatorExploreListAllQueries(
        {
            ...poolParams,
            platforms: platform,
            page_size: PAGE_SIZE,
        },
        { enabled: isEnabled }
    );

    // The pool's own member_count counts platform accounts rather than creators, so the members the
    // campaign's platform hides can only be told from a second count of the pool without the filter.
    const { data: unfilteredPool, isLoading: isUnfilteredCountLoading } = useCreatorSearch(
        { ...poolParams, page_size: POOL_COUNT_PAGE_SIZE },
        { enabled: isEnabled }
    );

    const unfilteredCount = unfilteredPool?.count;
    const hiddenCount = isNumber(unfilteredCount) && isNumber(count) ? Math.max(unfilteredCount - count, 0) : undefined;

    // Avatars are only worth fetching once the member list has settled.
    const { tiktokUserIds, instagramUserIds } = extractUserIdsFromData(areAllPagesResolved ? data : []);

    const tiktokImageQueries = useTiktokUserImageQueriesByIds(tiktokUserIds.length ? [tiktokUserIds] : []);
    const instagramImageQueries = useInstagramUserImageQueriesByIds(instagramUserIds.length ? [instagramUserIds] : []);

    const tiktokUserImages = tiktokImageQueries.flatMap((query) => query.data ?? []);
    const instagramUserImages = instagramImageQueries.flatMap((query) => query.data ?? []);

    const getCreatorImages = (creator: influencer.CreatorSearchResult | {} | null | undefined) => {
        if (!creator || !('platforms' in creator)) {
            return { tiktok: null, instagram: null };
        }

        const tiktokImage = tiktokUserImages.find(
            (image) => image.user_id === creator.platforms.tiktok[0]?.creatorbase_user_id
        );
        const instagramImage = instagramUserImages.find(
            (image) => image.user_id === creator.platforms.instagram[0]?.creatorbase_user_id
        );

        return {
            tiktok: tiktokImage?.avatar_thumb.cached_url ?? tiktokImage?.avatar_thumb.original_url,
            instagram: instagramImage?.avatar_thumb.cached_url ?? instagramImage?.avatar_thumb.original_url,
        };
    };

    const areCreatorImagesLoading =
        tiktokImageQueries.some((query) => query.isLoading) || instagramImageQueries.some((query) => query.isLoading);

    return {
        creators: data,
        count,
        /** Pool creators with no account on the campaign's platform, so absent from the table. */
        hiddenCount,
        areAllPagesResolved,
        isLoading,
        /** Kept apart from `isLoading`: this count lands on its own, after the first page of creators. */
        isHiddenCountLoading: isUnfilteredCountLoading,
        isFetching,
        hasError,
        getCreatorImages,
        getAreCreatorImagesLoading: () => areCreatorImagesLoading,
    };
}
